import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

/**
 * Upsert Product Supercede
 *
 * Takes ONE supercede relationship as single-value input sockets, shaped after
 * the Gently model (`im_product_supercedes`) — not after any source system. A
 * CDC observer or file parser maps its own columns into these sockets, so the
 * same step serves every source; anything source-specific (field naming,
 * sentinel values, chain flattening) belongs in that upstream mapping.
 *
 * Unlike the other procurement upserts this one is a PRODUCT-TO-PRODUCT edge:
 * there is no warehouse, and it resolves TWO SKUs. The conflict target is
 * `(organization, oldProduct, newProduct)`.
 *
 * Both SKUs resolve against the product catalog (optionally scoped by the
 * `originSystem` config); rows where either side is unknown are skipped and
 * reported per input, never auto-created. A row whose old and new SKU are the
 * same is also skipped — a product cannot supercede itself, and the DB would
 * happily store that self-loop.
 *
 * With a `batch` config the engine hands whole chunks to the bulk handler. A
 * leaf step: per-row outputs are execution results, not a downstream data
 * contract.
 */
export const upsertProductSupercedeStep = defineStepConfig({
  type: "upsert_product_supercede",
  name: "Upsert Product Supercede",
  description:
    "Upserts one product supercede edge (old SKU -> new SKU, roll-on-hand, status) into im_product_supercedes. Unknown SKUs and self-supercedes are skipped and reported, never created.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    oldProductSku: z
      .string()
      .describe("SKU being replaced (resolved to the old product)"),
    newProductSku: z
      .string()
      .describe("Replacement SKU (resolved to the new product)"),
    rollOnHand: z
      .boolean()
      .optional()
      .describe(
        "Whether on-hand stock rolls forward to the replacement. Omitted keeps the stored value on an existing edge, or false on a new one.",
      ),
    status: z
      .string()
      .optional()
      .describe(
        "Edge status (e.g. `active`). Omitted keeps the stored value, or defaults to `active` on a new edge.",
      ),
    originId: z
      .string()
      .nullable()
      .optional()
      .describe("Source record identifier, for traceability"),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The supercede edge was upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped because it could not be resolved"),
    unresolvedOldProduct: z
      .boolean()
      .describe("No product matched the old SKU"),
    unresolvedNewProduct: z
      .boolean()
      .describe("No product matched the new SKU"),
    selfSupercede: z
      .boolean()
      .describe("Old and new SKU resolved to the same product — skipped"),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Stamped on written rows, and restricts the legacy ProductV2 lookup. Does not scope product resolution: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
